'use client';

import type {ComboboxOption} from 'common/components/combobox_input';
import {ComboboxInput} from 'common/components/combobox_input';
import {getAllCompanies} from 'iq-companies/api';
import {getPositions} from 'iq-positions/api';
import {OUTCOME_OPTIONS, OUTCOME_SELECTED_CLASSES} from 'iq-interview_experiences/constants';
import type {SubmissionFlowData} from 'iq-interview_experiences/types';
import {FlowDateField} from './flow_date_field';
import {
  FlowStepHeading,
  FlowLabel,
  FlowInput,
  FlowPillButton,
  FlowValidationError,
} from './flow_form';

type Props = {
  step: number;
  totalSteps: number;
  data: SubmissionFlowData;
  onField: <K extends keyof SubmissionFlowData>(key: K, value: SubmissionFlowData[K]) => void;
  validationError: string | null;
};

export function FlowStepDetails({step, totalSteps, data, onField, validationError}: Props) {
  async function fetchCompanyOptions(): Promise<ComboboxOption<string>[]> {
    const companies = await getAllCompanies();
    return companies.map((c) => ({value: c.slug, label: c.name}));
  }

  async function fetchPositionOptions(): Promise<ComboboxOption<string>[]> {
    const positions = await getPositions();
    return positions.map((p) => ({value: p.slug, label: p.name}));
  }

  return (
    <div className='flex flex-col gap-5'>
      <FlowStepHeading step={step} totalSteps={totalSteps}>
        Let&apos;s start with the basics. Which company, what role, what was the outcome, and when
        was the interview?
      </FlowStepHeading>

      <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
        <ComboboxInput
          label='Company'
          placeholder='e.g. Spotify'
          value={data.company?.label ?? ''}
          onChange={(option) => onField('company', option)}
          fetchOptions={fetchCompanyOptions}
        />
        <ComboboxInput
          label='Position'
          placeholder='e.g. Data Scientist'
          value={data.position?.label ?? ''}
          onChange={(option) => onField('position', option)}
          fetchOptions={fetchPositionOptions}
        />
      </div>

      <FlowDateField
        customDate={data.customDate}
        onCustomDateChange={(v) => onField('customDate', v)}
      />

      <div className='flex flex-col gap-1.5'>
        <FlowLabel>Location</FlowLabel>
        <FlowInput
          type='text'
          value={data.location}
          onChange={(e) => onField('location', e.target.value)}
          placeholder='e.g. San Francisco, CA or Remote'
        />
      </div>

      <div className='flex flex-col gap-2'>
        <FlowLabel>Outcome</FlowLabel>
        <div className='flex gap-2 flex-wrap'>
          {OUTCOME_OPTIONS.map((opt) => (
            <FlowPillButton
              key={opt.value}
              selected={data.outcome === opt.value}
              selectedClassName={OUTCOME_SELECTED_CLASSES[opt.value]}
              onClick={() => onField('outcome', opt.value)}>
              {opt.label}
            </FlowPillButton>
          ))}
        </div>
      </div>

      {validationError && <FlowValidationError message={validationError} />}
    </div>
  );
}
